// Pure statistical routines for the exploratory analysis module.
// These produce numbers/objects only — HTML rendering lives in main.ts.

import { num, hasValue, uniqueValues, sigmoid, clamp, type Row } from "./util";
import { rowValue, getFieldMeta } from "./schema";

export type VariableType = "binary" | "numeric" | "categorical";

export interface Summary {
  n: number;
  mean: number;
  variance: number;
  sd: number;
  median: number;
  q1: number;
  q3: number;
  min: number;
  max: number;
}

export interface MethodRecommendation {
  primary: string;
  rows: number;
  usableRows: number;
  groups: string[];
  groupCounts: { group: string; n: number }[];
  outcomeType: VariableType;
  minGroupN: number;
  covariates: string[];
  canAncova: boolean;
  canPsm: boolean;
  notes: string[];
}

export interface DesignMatrix {
  X: number[][];
  names: string[];
}

export interface OlsFit {
  beta: number[];
  se: number[];
  df: number;
  r2: number;
}

export interface PairwiseResult {
  group: string;
  nRef?: number;
  nTest?: number;
  effect?: number;
  p?: number;
  method?: string;
  note?: string;
}

export interface MatchPair {
  treated: number;
  control: number;
  dist: number;
}

export function summary(values: number[]): Summary {
  const arr = values.filter(Number.isFinite).sort((a, b) => a - b);
  const n = arr.length;
  if (!n) return { n: 0, mean: NaN, variance: NaN, sd: NaN, median: NaN, q1: NaN, q3: NaN, min: NaN, max: NaN };
  const mean = arr.reduce((sum, value) => sum + value, 0) / n;
  const variance = n > 1 ? arr.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / (n - 1) : 0;
  return {
    n,
    mean,
    variance,
    sd: Math.sqrt(variance),
    median: quantile(arr, 0.5),
    q1: quantile(arr, 0.25),
    q3: quantile(arr, 0.75),
    min: arr[0],
    max: arr[n - 1],
  };
}

export function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return NaN;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  return sorted[base + 1] !== undefined ? sorted[base] + rest * (sorted[base + 1] - sorted[base]) : sorted[base];
}

export function inferVariableType(rows: Row[], id: string): VariableType {
  const values = rows.map((row) => rowValue(row, id)).filter(hasValue);
  const unique = uniqueValues(values);
  if (unique.length === 2) return "binary";
  const numericCount = values.filter((value) => Number.isFinite(num(value))).length;
  if (values.length && numericCount / values.length >= 0.8) return "numeric";
  return unique.length <= 2 ? "binary" : "categorical";
}

export function buildMethodRecommendation(rows: Row[], groupId: string, outcomeId: string, covariates: string[]): MethodRecommendation {
  const analysisRows = rows.filter((row) => hasValue(rowValue(row, groupId)) && hasValue(rowValue(row, outcomeId)));
  const groups = uniqueValues(analysisRows.map((row) => rowValue(row, groupId)));
  const outcomeType = inferVariableType(analysisRows, outcomeId);
  const groupCounts = groups.map((group) => ({ group, n: analysisRows.filter((row) => rowValue(row, groupId) === group).length }));
  const minGroupN = groupCounts.length ? Math.min(...groupCounts.map((item) => item.n)) : 0;
  let primary = "descriptive";
  const notes: string[] = [];

  if (analysisRows.length < rows.length) {
    notes.push(`Исключено строк с пропусками по группе/исходу: ${rows.length - analysisRows.length}.`);
  }
  if (groups.length !== 2) {
    notes.push(`Сейчас уровней группирующего фактора: ${groups.length}. Для t-test, Fisher, ANCOVA и PSM в этом модуле нужна бинарная экспозиция.`);
  } else if (outcomeType === "numeric") {
    primary = minGroupN >= 10 ? "ttest" : "descriptive";
    notes.push(minGroupN >= 30
      ? "Для количественного исхода и двух групп подходит Welch t-test; при клинически важных ковариатах смотрите ANCOVA."
      : "Группы небольшие: t-test можно считать ориентировочно, но в публикационном анализе стоит проверить распределения и рассмотреть непараметрический тест.");
  } else if (outcomeType === "binary") {
    primary = "fisher";
    notes.push("Для бинарного исхода и двух групп безопасный старт — критерий Фишера 2x2, особенно при малых ожидаемых частотах.");
  } else {
    notes.push("Исход выглядит категориальным с более чем двумя уровнями; нужен χ2/точный многокатегориальный тест или модель вне браузерного модуля.");
  }
  if (covariates.length && groups.length === 2 && outcomeType === "numeric") {
    notes.push("ANCOVA здесь считается как линейная модель: outcome ~ group + covariates.");
  }
  if (covariates.length && groups.length === 2) {
    notes.push("PSM здесь использует логистическую модель propensity score и nearest-neighbor matching без замены.");
  }

  return {
    primary,
    rows: rows.length,
    usableRows: analysisRows.length,
    groups,
    groupCounts,
    outcomeType,
    minGroupN,
    covariates,
    canAncova: groups.length === 2 && outcomeType === "numeric",
    canPsm: groups.length === 2 && covariates.length > 0,
    notes,
  };
}

export function buildDesignMatrix(rows: Row[], covariates: string[]): DesignMatrix {
  const columns: number[][] = [];
  const names: string[] = [];
  covariates.forEach((id) => {
    const field = getFieldMeta(id);
    if (!field) return;
    if (field.type === "select") {
      const levels = uniqueValues(rows.map((row) => rowValue(row, id)));
      if (levels.length <= 1) return;
      levels.slice(1).forEach((level) => {
        columns.push(rows.map((row) => (rowValue(row, id) === level ? 1 : 0)));
        names.push(`${id}=${level}`);
      });
    } else {
      const values = rows.map((row) => num(rowValue(row, id)));
      if (values.some((value) => !Number.isFinite(value))) return;
      const s = summary(values);
      if (!Number.isFinite(s.sd) || s.sd === 0) return;
      columns.push(values.map((value) => (value - s.mean) / s.sd));
      names.push(`${id} (z)`);
    }
  });
  const X = rows.map((_, rowIndex) => columns.map((column) => column[rowIndex]));
  return { X, names };
}

export function ols(X: number[][], y: number[]): OlsFit {
  const Xt = transpose(X);
  const XtX = multiply(Xt, X);
  const XtXInv = inverse(XtX);
  const beta = multiplyVec(multiply(XtXInv, Xt), y);
  const fitted = X.map((row) => dot(row, beta));
  const residuals = y.map((value, i) => value - fitted[i]);
  const n = y.length;
  const p = beta.length;
  const df = n - p;
  if (df <= 0) throw new Error("Недостаточно степеней свободы для модели.");
  const rss = residuals.reduce((sum, value) => sum + value * value, 0);
  const tss = y.reduce((sum, value) => sum + Math.pow(value - summary(y).mean, 2), 0);
  const sigma2 = rss / df;
  const se = XtXInv.map((row, i) => Math.sqrt(Math.max(0, row[i] * sigma2)));
  return { beta, se, df, r2: tss > 0 ? 1 - rss / tss : NaN };
}

export function logisticScores(Xraw: number[][], y: number[]): number[] {
  const X = Xraw.map((row) => [1, ...row]);
  const beta = new Array(X[0].length).fill(0);
  const lr = 0.08;
  const lambda = 0.01;
  for (let iter = 0; iter < 3500; iter++) {
    const grad = new Array(beta.length).fill(0);
    X.forEach((row, i) => {
      const p = sigmoid(dot(row, beta));
      row.forEach((value, j) => {
        grad[j] += ((p - y[i]) * value) / X.length;
      });
    });
    for (let j = 1; j < beta.length; j++) grad[j] += (lambda * beta[j]) / X.length;
    for (let j = 0; j < beta.length; j++) beta[j] -= lr * grad[j];
  }
  return X.map((row) => clamp(sigmoid(dot(row, beta)), 0.001, 0.999));
}

export function nearestMatches(_rows: Row[], treatment: number[], logits: number[], caliper: number): MatchPair[] {
  const treated = treatment.map((value, index) => (value === 1 ? index : -1)).filter((index) => index >= 0);
  const controls = treatment.map((value, index) => (value === 0 ? index : -1)).filter((index) => index >= 0);
  const unused = new Set(controls);
  const pairs: MatchPair[] = [];
  treated
    .map((index) => ({ index, score: logits[index] }))
    .sort((a, b) => a.score - b.score)
    .forEach(({ index }) => {
      let bestControl = -1;
      let bestDist = Infinity;
      unused.forEach((control) => {
        const dist = Math.abs(logits[index] - logits[control]);
        if (dist <= caliper && dist < bestDist) {
          bestControl = control;
          bestDist = dist;
        }
      });
      if (bestControl >= 0) {
        unused.delete(bestControl);
        pairs.push({ treated: index, control: bestControl, dist: bestDist });
      }
    });
  return pairs;
}

export function psmOutcomeEffect(rows: Row[], matches: MatchPair[], outcomeId: string): { value: number; label: string } {
  const values = matches.map((pair) => ({ t: rowValue(rows[pair.treated], outcomeId), c: rowValue(rows[pair.control], outcomeId) }));
  const type = inferVariableType(rows, outcomeId);
  if (type === "numeric") {
    const diffs = values.map((pair) => num(pair.t) - num(pair.c)).filter(Number.isFinite);
    return { value: summary(diffs).mean, label: "средняя парная разность treated - control" };
  }
  const levels = uniqueValues(values.flatMap((pair) => [pair.t, pair.c]));
  const event = levels[1] ?? levels[0];
  const diffs = values.map((pair) => (pair.t === event ? 1 : 0) - (pair.c === event ? 1 : 0));
  return { value: summary(diffs).mean, label: `risk difference для события ${event}` };
}

export function maxAbsSmd(X: number[][], treatment: number[]): number {
  if (!X.length || !X[0]?.length) return NaN;
  let max = 0;
  for (let j = 0; j < X[0].length; j++) {
    const treated = X.filter((_, i) => treatment[i] === 1).map((row) => row[j]);
    const control = X.filter((_, i) => treatment[i] === 0).map((row) => row[j]);
    const st = summary(treated);
    const sc = summary(control);
    const pooled = Math.sqrt((st.variance + sc.variance) / 2);
    const smd = pooled ? Math.abs((st.mean - sc.mean) / pooled) : 0;
    if (Number.isFinite(smd)) max = Math.max(max, smd);
  }
  return max;
}

export function pairwiseT(rows: Row[], groupId: string, outcomeId: string, ref: string, group: string): PairwiseResult {
  const refValues = rows.filter((row) => rowValue(row, groupId) === ref).map((row) => num(rowValue(row, outcomeId))).filter(Number.isFinite);
  const testValues = rows.filter((row) => rowValue(row, groupId) === group).map((row) => num(rowValue(row, outcomeId))).filter(Number.isFinite);
  if (refValues.length < 2 || testValues.length < 2) return { group, nRef: refValues.length, nTest: testValues.length, effect: NaN, p: NaN, method: "мало n" };
  const sr = summary(refValues);
  const st = summary(testValues);
  const effect = st.mean - sr.mean;
  const se = Math.sqrt(sr.variance / sr.n + st.variance / st.n);
  const t = effect / se;
  const df = Math.pow(sr.variance / sr.n + st.variance / st.n, 2) /
    (Math.pow(sr.variance / sr.n, 2) / (sr.n - 1) + Math.pow(st.variance / st.n, 2) / (st.n - 1));
  const p = 2 * (1 - studentTCdf(Math.abs(t), df));
  return { group, nRef: sr.n, nTest: st.n, effect, p, method: "Welch t-test" };
}

export function pairwiseFisher(rows: Row[], groupId: string, outcomeId: string, ref: string, group: string): PairwiseResult {
  const outcomes = uniqueValues(rows.map((row) => rowValue(row, outcomeId)));
  if (outcomes.length !== 2) return { group, method: "не 2x2" };
  const table2 = [
    [
      rows.filter((row) => rowValue(row, groupId) === ref && rowValue(row, outcomeId) === outcomes[0]).length,
      rows.filter((row) => rowValue(row, groupId) === ref && rowValue(row, outcomeId) === outcomes[1]).length,
    ],
    [
      rows.filter((row) => rowValue(row, groupId) === group && rowValue(row, outcomeId) === outcomes[0]).length,
      rows.filter((row) => rowValue(row, groupId) === group && rowValue(row, outcomeId) === outcomes[1]).length,
    ],
  ];
  const nRef = table2[0][0] + table2[0][1];
  const nTest = table2[1][0] + table2[1][1];
  const riskRef = nRef ? table2[0][1] / nRef : NaN;
  const riskTest = nTest ? table2[1][1] / nTest : NaN;
  return { group, nRef, nTest, effect: riskTest - riskRef, p: fisherExact2x2(table2), method: "Fisher exact" };
}

export function fisherExact2x2(table2: number[][]): number {
  const [[a, b], [c, d]] = table2;
  const row1 = a + b;
  const row2 = c + d;
  const col1 = a + c;
  const total = row1 + row2;
  const min = Math.max(0, col1 - row2);
  const max = Math.min(col1, row1);
  const observed = hypergeomProb(a, row1, col1, total);
  let p = 0;
  for (let x = min; x <= max; x++) {
    const prob = hypergeomProb(x, row1, col1, total);
    if (prob <= observed + 1e-12) p += prob;
  }
  return Math.min(1, p);
}

export function hypergeomProb(x: number, row1: number, col1: number, total: number): number {
  return Math.exp(logChoose(col1, x) + logChoose(total - col1, row1 - x) - logChoose(total, row1));
}

export function logChoose(n: number, k: number): number {
  if (k < 0 || k > n) return -Infinity;
  return logGamma(n + 1) - logGamma(k + 1) - logGamma(n - k + 1);
}

export function studentTCdf(t: number, df: number): number {
  if (!Number.isFinite(t) || !Number.isFinite(df) || df <= 0) return NaN;
  const x = df / (df + t * t);
  const ib = regularizedBeta(x, df / 2, 0.5);
  return t >= 0 ? 1 - 0.5 * ib : 0.5 * ib;
}

export function regularizedBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const bt = Math.exp(logGamma(a + b) - logGamma(a) - logGamma(b) + a * Math.log(x) + b * Math.log(1 - x));
  if (x < (a + 1) / (a + b + 2)) return (bt * betaContinuedFraction(x, a, b)) / a;
  return 1 - (bt * betaContinuedFraction(1 - x, b, a)) / b;
}

export function betaContinuedFraction(x: number, a: number, b: number): number {
  const maxIter = 100;
  const eps = 3e-7;
  const fpmin = 1e-30;
  const qab = a + b;
  const qap = a + 1;
  const qam = a - 1;
  let c = 1;
  let d = 1 - (qab * x) / qap;
  if (Math.abs(d) < fpmin) d = fpmin;
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= maxIter; m++) {
    const m2 = 2 * m;
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < fpmin) d = fpmin;
    c = 1 + aa / c;
    if (Math.abs(c) < fpmin) c = fpmin;
    d = 1 / d;
    h *= d * c;
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < fpmin) d = fpmin;
    c = 1 + aa / c;
    if (Math.abs(c) < fpmin) c = fpmin;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < eps) break;
  }
  return h;
}

export function logGamma(z: number): number {
  const cof = [
    57.1562356658629235, -59.5979603554754912, 14.1360979747417471,
    -0.491913816097620199, 0.0000339946499848118887,
    0.0000465236289270485756, -0.0000983744753048795646,
    0.000158088703224912494, -0.000210264441724104883,
    0.000217439618115212643, -0.00016431810653676389,
    0.0000844182239838527433, -0.0000261908384015814087,
    0.00000368991826595316234,
  ];
  let x = 0.999999999999997092;
  for (let i = 0; i < cof.length; i++) x += cof[i] / (z + i + 1);
  // Lanczos g = 671/128 = 5.2421875 (Numerical Recipes, 3rd ed. gammln).
  // The original index.html used `z + cof.length - 0.5`, which is incorrect and
  // produced wrong log-gamma values — and therefore wrong p-values everywhere
  // (Fisher, t-test, ANCOVA, pairwise). Fixed here; means/SD/effects/PSM
  // balance were never affected because they do not use logGamma.
  const t = z + 5.2421875;
  return 0.9189385332046727 + (z + 0.5) * Math.log(t) - t + Math.log(x / z);
}

export function transpose(A: number[][]): number[][] {
  return A[0].map((_, col) => A.map((row) => row[col]));
}

export function multiply(A: number[][], B: number[][]): number[][] {
  return A.map((row) => B[0].map((_, j) => row.reduce((sum, value, k) => sum + value * B[k][j], 0)));
}

export function multiplyVec(A: number[][], v: number[]): number[] {
  return A.map((row) => dot(row, v));
}

export function dot(a: number[], b: number[]): number {
  return a.reduce((sum, value, i) => sum + value * b[i], 0);
}

export function inverse(A: number[][]): number[][] {
  const n = A.length;
  const M = A.map((row, i) => [...row, ...Array.from({ length: n }, (_, j) => (i === j ? 1 : 0))]);
  for (let i = 0; i < n; i++) {
    let pivot = i;
    for (let r = i + 1; r < n; r++) if (Math.abs(M[r][i]) > Math.abs(M[pivot][i])) pivot = r;
    if (Math.abs(M[pivot][i]) < 1e-10) throw new Error("Матрица модели вырождена: уберите избыточные ковариаты.");
    [M[i], M[pivot]] = [M[pivot], M[i]];
    const div = M[i][i];
    for (let j = 0; j < 2 * n; j++) M[i][j] /= div;
    for (let r = 0; r < n; r++) {
      if (r === i) continue;
      const factor = M[r][i];
      for (let j = 0; j < 2 * n; j++) M[r][j] -= factor * M[i][j];
    }
  }
  return M.map((row) => row.slice(n));
}
